import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Col, Button, Modal, ModalHeader, ModalBody, ModalFooter, Row, Form, FormGroup, Label, Input } from 'reactstrap';
import { removeFromOrder, updateOrderQuantity, clearCurrentOrder } from '../../../slices/order/orderSlice';
import { DownloadBill } from './DownloadBill';
import { PrintBill } from './PrintBill';
import { BillContent } from './BillContent';
import { Link, useNavigate } from 'react-router-dom';
import './OrderMenu.css';
import OrderManagementService from '../../../Services/QRCodeServices/Order Management/OrderManagementService';
import { toast } from 'react-hot-toast';

export const OrderMenu = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [customerContact, setCustomerContact] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [paymentStatus, setPaymentStatus] = useState('unpaid');
  const [userInfo, setUserInfo] = useState(null);
  const [orgCode, setOrgCode] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const { currentOrder } = useSelector(state => state.Order);

  useEffect(() => {
    const storedUserInfo = localStorage.getItem("user");
    if (storedUserInfo) {
      const parsedUserInfo = JSON.parse(storedUserInfo);
      setUserInfo(parsedUserInfo);
      setOrgCode(parsedUserInfo?.organizationInfo?.orgCode || null);
    }
  }, []);

  const calculateTotalAmountBeforeDiscount = () => {
    return currentOrder.reduce((total, item) => {
      const originalPrice = item.price / (1 - (item.discountDetail?.discountPercentage || 0) / 100);
      return total + originalPrice * item.quantity;
    }, 0);
  };

  const calculateTotalDiscount = () => {
    return currentOrder.reduce((total, item) => {
      const originalPrice = item.price / (1 - (item.discountDetail?.discountPercentage || 0) / 100);
      const discount = (originalPrice - item.price) * item.quantity;
      return total + discount;
    }, 0);
  };

  const totalAmountBeforeDiscount = calculateTotalAmountBeforeDiscount();
  const totalDiscount = calculateTotalDiscount();
  const finalAmount = totalAmountBeforeDiscount - totalDiscount;

  const handleIncrement = (item) => {
    dispatch(updateOrderQuantity({
      itemId: item.id,
      quantity: item.quantity + 1
    }));
  };

  const handleDecrement = (item) => {
    if (item.quantity > 1) {
      dispatch(updateOrderQuantity({
        itemId: item.id,
        quantity: item.quantity - 1
      }));
    }
  };

  const handleRemove = (itemId) => {
    dispatch(removeFromOrder({ itemId }));
  };

  const handleClearAll = () => {
    dispatch(clearCurrentOrder());
  };

  const validateOrder = () => {
    if (!orgCode) {
      setError("Organization code not found. Please try logging in again.");
      return false;
    }
    if (currentOrder.length === 0) {
      setError("Your order is empty.");
      return false;
    }
    return true;
  };

  const handlePlaceOrder = async () => {
    if (!validateOrder()) return;
    if (!customerContact.trim()) {
      setError("Please enter customer contact information");
      return;
    }
  
    setLoading(true);
    setError(null);
  
    const orderPayload = {
      customerContact: customerContact.trim(),
      items: currentOrder.map(item => ({
        menuItemId: item.id,
        quantity: item.quantity
      }))
    };
  
    try {
      console.log('Sending order payload:', orderPayload);
      const response = await OrderManagementService.addOrder(orgCode, orderPayload);
      console.log('Order response:', response);
      
      if (response) {
        toast.success("Order placed successfully!!");
        dispatch(clearCurrentOrder());
        setCustomerContact('');
      }
      
    } catch (error) {
      console.error("Error placing order:", error);
      setError(error.message || "Failed to place order. Please try again.");
      toast.error("Failed to place order. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Col className="mb-3">
      {error && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          {error}
          <button type="button" className="btn-close" onClick={() => setError(null)}></button>
        </div>
      )}
      <Row className="mb-2">
        <Col md={12} xs={12}>
          <input
            type="text"
            className="form-control"
            placeholder="Customer Contact"
            value={customerContact}
            onChange={(e) => setCustomerContact(e.target.value)}
            required
          />
        </Col>
      </Row>

      {/* Order Items */}
      {currentOrder.map((item) => (
        <div key={item.id} className="card border-0 shadow-sm mb-3">
          <div className="card-body p-2">
            <div className="d-flex align-items-center gap-3">
              <div className="order-menu-image flex-shrink-0">
                <img
                  src={item.imageUrl || 'placeholder-image-url'}
                  alt={item.title}
                  className="rounded"
                />
              </div>
              <div className="flex-grow-1">
                <div className="mb-2">
                  <h5 className="mb-1">{item.title}</h5>
                  <p className="text-success fw-medium m-0 p-0">
                    Rs. {item.price}
                    {item.discountDetail && (
                      <span className="text-danger ms-2">
                        ({item.discountDetail.discountPercentage}% OFF)
                      </span>
                    )}
                  </p>
                  <p className="text-muted mb-0">Total: Rs. {(item.price * item.quantity).toFixed(2)}</p>
                </div>
                <div className="d-flex justify-content-between">
                  <div className="btn-group quantity-controls">
                    <Button size="sm" outline onClick={() => handleDecrement(item)}>-</Button>
                    <Button size="sm" outline className="px-3 disabled">{item.quantity}</Button>
                    <Button size="sm" outline onClick={() => handleIncrement(item)}>+</Button>
                  </div>
                  <Button
                    color="danger"
                    size="sm"
                    outline
                    className="d-flex align-items-center gap-1"
                    onClick={() => handleRemove(item.id)}
                  >
                    <i className="ri-delete-bin-line"></i>
                    {/* <span>Remove</span> */}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      ))}

      {/* Order Summary */}
      {currentOrder.length > 0 ? (
        <div className="card border-0 shadow-sm p-3">
          <h6 className="mb-3">Order Summary</h6>
          <div className="d-flex justify-content-between mb-2">
            <p className="mb-0">Total Amount Before Discount:</p>
            <p className="mb-0 fw-bold">Rs. {totalAmountBeforeDiscount.toFixed(2)}</p>
          </div>
          <div className="d-flex justify-content-between mb-2">
            <p className="mb-0">Total Discount:</p>
            <p className="mb-0 fw-bold text-danger">Rs. {totalDiscount.toFixed(2)}</p>
          </div>
          <hr className="p-0 m-0" />
          <div className="d-flex justify-content-between mb-2">
            <p className="mb-0">Final Amount:</p>
            <p className="mb-0 fw-bold">Rs. {finalAmount.toFixed(2)}</p>
          </div>

          <div className="d-flex justify-content-between gap-2 mt-3">
            <Button className="w-100" color="secondary" onClick={() => setIsModalOpen(true)}>
              View Bill
            </Button>
            <Button color="danger" className="w-100" onClick={handleClearAll}>
              <i className="ri-delete-bin-line"></i> Clear All
            </Button>
          </div>

          <Button 
            color="primary" 
            className="w-100 mt-3" 
            onClick={handlePlaceOrder}
            disabled={loading}
          >
            {loading ? (
              <span>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Processing...
              </span>
            ) : (
              'Place Order'
            )}
          </Button>
        </div>
      ) : (
        <div className="text-center p-4">
          <p className="text-muted mb-0">No items in order</p>
        </div>
      )}

      {/* Bill Modal */}
      <Modal isOpen={isModalOpen} toggle={() => setIsModalOpen(!isModalOpen)} size="md">
        <ModalHeader toggle={() => setIsModalOpen(!isModalOpen)} />
        <ModalBody>
          <BillContent
            customerName={customerContact}
            currentOrder={currentOrder}
            totalAmountBeforeDiscount={totalAmountBeforeDiscount}
            totalDiscount={totalDiscount}
            finalAmount={finalAmount}
          />
        </ModalBody>
        <ModalFooter>
          <DownloadBill
            currentOrder={currentOrder}
            totalAmountBeforeDiscount={totalAmountBeforeDiscount}
            customerName={customerContact}
            totalDiscount={totalDiscount}
            finalAmount={finalAmount}
          />
          <PrintBill
            currentOrder={currentOrder}
            totalAmountBeforeDiscount={totalAmountBeforeDiscount}
            customerName={customerContact}
            totalDiscount={totalDiscount}
            finalAmount={finalAmount}
          />
          <Button color="secondary" onClick={() => setIsModalOpen(false)}>
            Close
          </Button>
        </ModalFooter>
      </Modal>
    </Col>
  );
};